import type {
  AudioVideoObserver,
  MeetingSessionStatus,
  VideoDownlinkObserver,
  VideoPriorityBasedPolicy,
  VideoSource,
} from "amazon-chime-sdk-js";
import type { RosterType } from "amazon-chime-sdk-component-library-react";
import type {
  ConnectionQuality,
  ReactionEvent,
  TileState,
  TileTransitionReason,
  VideoSourceState,
} from "@core/features/video";
import {
  Layout,
  isContentShare,
  updateDownlinkPreferences,
  videoEventBus,
  GRACE_GOOD_MS,
  GRACE_POOR_MS,
  ROSTER_GRACE_MS,
  RECOVERY_COOLDOWN_MS,
  RECONNECT_GUARD_TIMEOUT_MS,
  MASS_DROP_VALIDATION_MS,
  INTENT_STALENESS_MS,
} from "@core/features/video";
import type ClientMetricReport from "amazon-chime-sdk-js/build/clientmetricreport/ClientMetricReport";
import { resolveAttendeeTileState } from "./resolveAttendeeTileState";

// ---------------------------------------------------------------------------
// Quality metrics — module-level ref written by metricsDidReceive (~1/sec),
// read by the heartbeat callback (~every 10s). Not in Zustand to avoid
// triggering renders on every metrics update.
// ---------------------------------------------------------------------------

export type RawQualityMetrics = {
  rtt_ms: number | null;
  available_outgoing_bitrate: number | null;
  available_incoming_bitrate: number | null;
  inbound_audio_packet_loss: number | null;
  inbound_audio_jitter: number | null;
  outbound_video_bitrate: number | null;
  outbound_video_fps: number | null;
  outbound_video_width: number | null;
  outbound_video_height: number | null;
  inbound_video_packet_loss: number | null;
  inbound_video_width: number | null;
  inbound_video_height: number | null;
  inbound_video_bitrate: number | null;
  outbound_video_quality_limitation: string | null;
  inbound_video_jitter: number | null;
  inbound_video_frames_dropped: number | null;
  outbound_audio_bitrate: number | null;
};

let latestQualityMetrics: RawQualityMetrics | null = null;

export function getLatestQualityMetrics(): RawQualityMetrics | null {
  return latestQualityMetrics;
}

// ---------------------------------------------------------------------------
// Rolling window of recent quality samples for the heartbeat-tick recovery
// path (plan Step 4 cause #1). Populated alongside `latestQualityMetrics`
// in `metricsDidReceive` — they share a single write path and cannot drift.
//
// Cleared whenever Zustand connectionQuality transitions to 'poor' (via
// setConnectionQualityWithWindowClear). Recovery requires the window to fill
// from scratch with 3 clean samples accumulated *since the last poor
// transition* — the window is always "samples observed during the current
// poor episode."
// ---------------------------------------------------------------------------

const RECOVERY_WINDOW_SIZE = 3;
let recentQualitySamples: RawQualityMetrics[] = [];

/**
 * Threshold mirrors the BE `is_degraded` definition exactly
 * (backend/core/lib/services/video_service.py:707-712, constants at :517-518).
 * If the BE threshold ever changes, this must change in lockstep.
 *
 * `null` values count as clean (matches BE's `is not None and >` shape).
 * BE checks `outbound_video_quality_limitation == 'bandwidth'` specifically;
 * `'cpu'` / `'other'` do not count as degraded.
 */
const RTT_THRESHOLD_MS = 500;
const PACKET_LOSS_THRESHOLD_PCT = 5.0;
function isSampleClean(sample: RawQualityMetrics): boolean {
  if (sample.rtt_ms !== null && sample.rtt_ms > RTT_THRESHOLD_MS) return false;
  if (sample.inbound_video_packet_loss !== null && sample.inbound_video_packet_loss > PACKET_LOSS_THRESHOLD_PCT) return false;
  if (sample.inbound_audio_packet_loss !== null && sample.inbound_audio_packet_loss > PACKET_LOSS_THRESHOLD_PCT) return false;
  if (sample.outbound_video_quality_limitation === 'bandwidth') return false;
  return true;
}

/**
 * Heartbeat-tick recovery: if Zustand connection_quality is 'poor' and
 * the rolling window is full of clean samples, force-flip to 'good'.
 * Closes the cause #1 failure mode where the SDK never fires
 * `connectionDidBecomeGood` after the trailing `Poor` (the BE-derived
 * `quality_degraded` thresholds and the SDK transport signal are
 * independent — clean transport metrics don't always produce a Good
 * event from the SDK). Called once per heartbeat tick at the
 * `getConnectionQuality` capture site in TranscriptionProvider.
 *
 * Returns the post-decision connection_quality value so the caller can
 * pass it through to the heartbeat hook in the same tick — guarantees
 * the heartbeat body reflects the recovered state immediately rather
 * than waiting for the next tick.
 *
 * Does not handle 'suggestStopVideo' — that signal is orthogonal to
 * transport metrics. See plan Step 4 cause #2 for that path.
 */
export function attemptHeartbeatTickRecovery(
  currentQuality: ConnectionQuality,
  setQuality: (q: ConnectionQuality) => void,
): ConnectionQuality {
  if (currentQuality !== 'poor') return currentQuality;
  if (recentQualitySamples.length < RECOVERY_WINDOW_SIZE) return currentQuality;
  if (!recentQualitySamples.every(isSampleClean)) return currentQuality;

  // All conditions met — recover.
  recentQualitySamples = [];
  setQuality('good');
  return 'good';
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DeviceStateMessage {
  camera: boolean;
  mic: boolean;
  timestamp: number;
}

interface IntentData {
  cameraIntentOn: boolean;
  micIntentOn: boolean;
  lastUpdate: number;
}

/** Store interface — the subset of platform store that ChimeObserverState needs. */
export interface ChimeObserverStore {
  // reads
  attendeeTileState: Record<string, TileState>;
  videoSourceState: VideoSourceState;
  layout: Layout;
  user: { id: number } | null;
  // writes — individual
  setAttendeeTileState: (id: string, state: TileState) => void;
  setAttendeeTileStates: (states: Record<string, TileState>) => void;
  removeAttendeeTileState: (id: string) => void;
  setConnectionQuality: (q: ConnectionQuality) => void;
  setActiveSpeakers: (speakers: string[]) => void;
  setRoster: (roster: RosterType) => void;
  addReaction: (attendeeId: string, reaction: ReactionEvent) => void;
  removeReaction: (attendeeId: string, reactionId: string) => void;
  // writes — atomic batch (for remoteVideoSourcesDidChange)
  setState: (fn: (s: any) => Partial<any>) => void;
}

// ---------------------------------------------------------------------------
// State machine class
// ---------------------------------------------------------------------------

export class ChimeObserverState {
  // --- Instance fields (formerly refs) ---
  sdkSourceSet = new Set<string>();
  tileAttendeeMap = new Map<number, string>();
  intentData = new Map<string, IntentData>();
  graceTimers = new Map<string, ReturnType<typeof setTimeout>>();
  rosterTimers = new Map<string, ReturnType<typeof setTimeout>>();
  // Separate from graceTimers — reactions have TTL semantics, not source-loss
  // semantics. Sharing graceTimers would cause source-loss events to cancel
  // reaction badges early and second reactions to evict the first one's timer.
  reactionTimers = new Set<ReturnType<typeof setTimeout>>();
  hadVideoSet = new Set<string>();
  bandwidthPaused = new Set<string>();

  connectionQuality: ConnectionQuality = 'good';
  isReconnecting = false;
  pendingSources: VideoSource[] | null = null;
  reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  massDropTimer: ReturnType<typeof setTimeout> | null = null;
  recoveryTimer: ReturnType<typeof setTimeout> | null = null;

  previousActiveSpeakers: string[] = [];

  // --- Bandwidth-aware preferences + share-start preemptive degradation ---
  /** Set of attendees the policy decided to suppress at the current
   *  bandwidth tier. Refreshed on every successful applyPreferenceUpdate.
   *  Consulted in tileWillBePausedByDownlinkPolicy to decide whether to
   *  tag the resulting tile state as 'bandwidth_suppressed' (intentional)
   *  vs 'frozen' (incidental SDK pause). */
  suppressionIntent = new Set<string>();
  /** Hysteresis: estimator-driven preference updates are rate-limited so
   *  active-speaker churn / downlink-policy callbacks don't spam the SDK
   *  with rapid-fire chooseRemoteVideoSources calls. Bypassed by share-
   *  start, local user actions, and mass-drop self-heal. */
  private static readonly PREFERENCE_COOLDOWN_MS = 3000;
  private lastPreferenceUpdateTs = 0;
  /** Set externally by useChimeObserver during init; null until set. */
  policyRef: { current: VideoPriorityBasedPolicy } | null = null;
  /** Preemptive-mode bookkeeping for share-start. */
  private static readonly PREEMPTIVE_WINDOW_MS = 5000;
  private static readonly PREEMPTIVE_RESTORE_THRESHOLD = 0.7;
  private preShareBitrate: number | null = null;
  private preemptiveModeActive = false;
  private preemptiveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private getStore: () => ChimeObserverStore,
    public getLocalAttendeeId: () => string | null
  ) {}

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  /**
   * Sole entry point for connection_quality state changes. Updates the
   * instance field, mirrors to Zustand, and clears the rolling-recovery
   * window on `'poor'` transitions.
   *
   * Window-clear invariant: clearing on every `'poor'` write means the
   * heartbeat-tick recovery requires 3 clean samples accumulated since
   * the most recent poor transition. The window is always "samples
   * observed during the current poor episode" — no stale data from a
   * prior episode can satisfy the recovery condition.
   */
  private setConnectionQualityWithWindowClear(next: ConnectionQuality): void {
    this.connectionQuality = next;
    this.getStore().setConnectionQuality(next);

    if (next === 'poor') {
      recentQualitySamples = [];
    }
  }

  /**
   * Sole entry point for `updateDownlinkPreferences` calls. Builds args
   * from instance state + Zustand + the latest quality metrics, applies
   * hysteresis (unless bypassed), and refreshes `suppressionIntent` from
   * the policy's return value so subsequent SDK pause callbacks can be
   * tagged 'bandwidth_suppressed' vs 'frozen'.
   *
   * Grep invariant: `updateDownlinkPreferences(` should appear only inside
   * this method. External callers (useLocalVideoSync, useActiveSpeaker-
   * DownlinkSync) reach the policy through here.
   */
  applyPreferenceUpdate(options?: { bypassHysteresis?: boolean }): void {
    if (!this.policyRef) return;

    if (options?.bypassHysteresis !== true) {
      const elapsed = Date.now() - this.lastPreferenceUpdateTs;
      if (elapsed < ChimeObserverState.PREFERENCE_COOLDOWN_MS) {
        return;
      }
    }

    const store = this.getStore();
    const metrics = latestQualityMetrics;
    const availableIncomingBitrateKbps =
      metrics?.available_incoming_bitrate != null
        ? metrics.available_incoming_bitrate / 1000
        : null;

    const result = updateDownlinkPreferences(
      {
        gridState: { layout: store.layout },
        videoSourceState: store.videoSourceState,
        attendeeTileState: store.attendeeTileState,
        availableIncomingBitrateKbps,
        preemptiveMode: this.preemptiveModeActive,
      },
      this.policyRef.current
    );
    this.lastPreferenceUpdateTs = Date.now();
    // Defense-in-depth: policy never adds local attendee, but assert.
    const localId = this.getLocalAttendeeId();
    if (localId) result.suppressedAttendees.delete(localId);
    this.suppressionIntent = result.suppressedAttendees;
  }

  /**
   * Called by `useLocalVideoSync` when `sharingAttendeeId` transitions
   * null → set. Snapshots current inbound bitrate, enters preemptive
   * mode (steps the next tier-resolution down by one), and schedules a
   * 5-second restore check: if inbound bitrate has recovered to ≥70%
   * of pre-share, exit preemptive mode and re-apply preferences.
   */
  notifyShareStart(): void {
    this.preShareBitrate = latestQualityMetrics?.inbound_video_bitrate ?? null;
    this.preemptiveModeActive = true;
    if (this.preemptiveTimer) clearTimeout(this.preemptiveTimer);
    this.preemptiveTimer = setTimeout(() => {
      this.preemptiveTimer = null;
      const currentInbound = latestQualityMetrics?.inbound_video_bitrate ?? 0;
      const preShare = this.preShareBitrate ?? 0;
      if (
        preShare > 0 &&
        currentInbound >= preShare * ChimeObserverState.PREEMPTIVE_RESTORE_THRESHOLD
      ) {
        this.preemptiveModeActive = false;
        this.applyPreferenceUpdate({ bypassHysteresis: true });
      }
      // Else: stay in preemptive mode. Subsequent estimator-driven calls
      // continue to read `preemptiveModeActive: true` and degrade. The
      // bandwidth tier itself will keep adapting via the metrics ref.
    }, ChimeObserverState.PREEMPTIVE_WINDOW_MS);
  }

  /**
   * Called by `useLocalVideoSync` when `sharingAttendeeId` transitions
   * set → null. Cancels the preemptive timer and clears the flag.
   */
  notifyShareStop(): void {
    if (this.preemptiveTimer) {
      clearTimeout(this.preemptiveTimer);
      this.preemptiveTimer = null;
    }
    this.preemptiveModeActive = false;
    this.preShareBitrate = null;
  }

  /**
   * Dev-only console-debug for tile state transitions. Zero runtime cost in
   * production builds (the body is gated on NODE_ENV). Skips no-op
   * transitions so steady-state never generates noise. Useful for local
   * development and Playwright debugging — admins read transitions through
   * the existing admin Status Logs surface, not through this.
   */
  private _debugTransition(params: {
    attendeeId: string;
    from: TileState;
    to: TileState;
    reason: TileTransitionReason;
  }): void {
    if (process.env.NODE_ENV === 'production') return;
    if (params.from === params.to) return;
    // eslint-disable-next-line no-console
    console.debug(
      `[ChimeObserverState] ${params.attendeeId.slice(0, 8)} ${params.from} → ${params.to} (${params.reason})`
    );
  }

  /** Transition a single attendee's tile state, cancelling any grace timer. */
  transitionAttendee(
    attendeeId: string,
    state: TileState,
    reason: TileTransitionReason
  ): void {
    const graceTimer = this.graceTimers.get(attendeeId);
    if (graceTimer) {
      clearTimeout(graceTimer);
      this.graceTimers.delete(attendeeId);
    }
    if (state === 'video') {
      this.hadVideoSet.add(attendeeId);
    }
    const store = this.getStore();
    const previousState =
      store.attendeeTileState[attendeeId] ?? 'absent';
    this._debugTransition({
      attendeeId,
      from: previousState,
      to: state,
      reason,
    });
    store.setAttendeeTileState(attendeeId, state);
  }

  /** Start a grace timer for an attendee — fires 'avatar' after graceMs. */
  startGraceTimer(attendeeId: string): void {
    const existing = this.graceTimers.get(attendeeId);
    if (existing) clearTimeout(existing);

    const graceMs = this.connectionQuality === 'good' ? GRACE_GOOD_MS : GRACE_POOR_MS;

    this.graceTimers.set(
      attendeeId,
      setTimeout(() => {
        this.graceTimers.delete(attendeeId);
        // Re-check: attendee may have reappeared during grace period
        if (this.sdkSourceSet.has(attendeeId)) return;
        if (this.connectionQuality !== 'good') return; // degraded — stay frozen
        const store = this.getStore();
        const previousState =
          store.attendeeTileState[attendeeId] ?? 'absent';
        this._debugTransition({
          attendeeId,
          from: previousState,
          to: 'avatar',
          reason: 'grace_timer_fired',
        });
        store.setAttendeeTileState(attendeeId, 'avatar');
      }, graceMs)
    );
  }

  /** Remove all internal state for a departed attendee. */
  purgeAttendee(attendeeId: string): void {
    this.rosterTimers.delete(attendeeId);
    this.intentData.delete(attendeeId);
    this.hadVideoSet.delete(attendeeId);
    this.bandwidthPaused.delete(attendeeId);
    const graceTimer = this.graceTimers.get(attendeeId);
    if (graceTimer) {
      clearTimeout(graceTimer);
      this.graceTimers.delete(attendeeId);
    }
    const store = this.getStore();
    const previousState = store.attendeeTileState[attendeeId];
    if (previousState !== undefined) {
      this._debugTransition({
        attendeeId,
        from: previousState,
        to: 'absent',
        reason: 'roster_drop',
      });
    }
    store.removeAttendeeTileState(attendeeId);
  }

  /**
   * Subscribe to attendee presence changes directly from the SDK.
   * This fires synchronously — before remoteVideoSourcesDidChange — ensuring
   * the roster timer gate is set before any source-change callbacks process
   * the departing attendee.
   */
  subscribeToPresence(audioVideo: {
    realtimeSubscribeToAttendeeIdPresence: (cb: (id: string, present: boolean, extId?: string, dropped?: boolean) => void) => void;
    realtimeUnsubscribeToAttendeeIdPresence: (cb: (id: string, present: boolean, extId?: string, dropped?: boolean) => void) => void;
  }): () => void {
    const handler = (
      attendeeId: string,
      present: boolean,
      _externalUserId?: string,
      _dropped?: boolean
    ) => {
      const localId = this.getLocalAttendeeId();
      if (attendeeId === localId) return;

      if (!present && !this.rosterTimers.has(attendeeId)) {
        const store = this.getStore();
        const { attendeeTileState } = store;
        const currentState = attendeeTileState[attendeeId];
        if (!currentState || currentState === 'absent') return;

        // Immediate avatar + grace timer cleanup + roster timer
        if (currentState === 'video' || currentState === 'frozen') {
          this._debugTransition({
            attendeeId,
            from: currentState,
            to: 'avatar',
            reason: 'roster_drop',
          });
          store.setAttendeeTileState(attendeeId, 'avatar');
        }
        const graceTimer = this.graceTimers.get(attendeeId);
        if (graceTimer) {
          clearTimeout(graceTimer);
          this.graceTimers.delete(attendeeId);
        }
        this.rosterTimers.set(
          attendeeId,
          setTimeout(() => this.purgeAttendee(attendeeId), ROSTER_GRACE_MS)
        );
      }

      if (present && this.rosterTimers.has(attendeeId)) {
        // Attendee returned — cancel removal timer
        const timer = this.rosterTimers.get(attendeeId);
        if (timer) clearTimeout(timer);
        this.rosterTimers.delete(attendeeId);
        // Restore to video if source available
        if (this.sdkSourceSet.has(attendeeId)) {
          this.transitionAttendee(attendeeId, 'video', 'source_reappeared');
        }
      }
    };

    audioVideo.realtimeSubscribeToAttendeeIdPresence(handler);
    return () => audioVideo.realtimeUnsubscribeToAttendeeIdPresence(handler);
  }

  /** Process a single attendee's state based on current inputs. */
  processAttendee(
    attendeeId: string,
    currentState: TileState,
    reason: TileTransitionReason
  ): void {
    if (this.rosterTimers.has(attendeeId)) return;
    const sourcePresent = this.sdkSourceSet.has(attendeeId);
    const intent = this.intentData.get(attendeeId);
    const intentFresh =
      intent !== undefined &&
      Date.now() - intent.lastUpdate <= INTENT_STALENESS_MS;
    const nextState = resolveAttendeeTileState({
      sourcePresent,
      intentCameraOn: intent?.cameraIntentOn,
      intentFresh,
      connectionQuality: this.connectionQuality,
      bandwidthPaused: this.bandwidthPaused.has(attendeeId),
      hadVideo: this.hadVideoSet.has(attendeeId),
    });

    if (nextState === currentState) return;

    // If transitioning to 'avatar' and had video, use grace timer
    if (
      nextState === 'avatar' &&
      this.hadVideoSet.has(attendeeId) &&
      !(intent?.cameraIntentOn === false && intentFresh)
    ) {
      // Don't start grace if connection is degraded — stay frozen
      if (this.connectionQuality !== 'good') {
        this.transitionAttendee(attendeeId, 'frozen', reason);
      } else {
        // Set to frozen during grace, then timer fires avatar
        this.transitionAttendee(attendeeId, 'frozen', reason);
        this.startGraceTimer(attendeeId);
      }
      return;
    }

    this.transitionAttendee(attendeeId, nextState, reason);
  }

  // -------------------------------------------------------------------------
  // Reconnect guard
  // -------------------------------------------------------------------------

  startReconnectGuard(): void {
    if (this.isReconnecting) return;
    this.isReconnecting = true;
    this.pendingSources = null;

    // Promote connection quality to 'poor' if currently good
    if (this.connectionQuality === 'good') {
      this.setConnectionQualityWithWindowClear('poor');
    }

    // Cancel all pending grace timers — everything stays frozen during reconnect
    for (const [, timer] of this.graceTimers) {
      clearTimeout(timer);
    }
    this.graceTimers.clear();

    // Clear SDK source set — stale during disconnect
    this.sdkSourceSet = new Set();

    // Freeze all remote attendees
    const store = this.getStore();
    const { attendeeTileState } = store;
    const localId = this.getLocalAttendeeId();
    const updates: Record<string, TileState> = { ...attendeeTileState };
    for (const id in updates) {
      if (id === localId) continue;
      if (this.rosterTimers.has(id)) continue;
      if (updates[id] === 'video') {
        this._debugTransition({
          attendeeId: id,
          from: 'video',
          to: 'frozen',
          reason: 'reconnect_guard',
        });
        updates[id] = 'frozen';
      }
    }
    store.setAttendeeTileStates(updates);

    // Safety timeout
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = setTimeout(() => {
      if (this.isReconnecting) {
        this.clearReconnectGuard();
      }
    }, RECONNECT_GUARD_TIMEOUT_MS);
  }

  clearReconnectGuard(): void {
    if (!this.isReconnecting) return;
    this.isReconnecting = false;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Reset connection quality if still degraded from reconnect guard.
    // If the SDK subsequently fires connectionDidBecomePoor, it will
    // re-set to 'poor' — this avoids permanent 'poor' after reconnect.
    if (this.connectionQuality === 'poor') {
      this.setConnectionQualityWithWindowClear('good');
    }

    // Re-evaluate all frozen tiles. In the audioVideoDidStart path, this
    // runs before pendingSources replay — tiles re-evaluate against an
    // empty sdkSourceSet and start grace timers. The subsequent
    // remoteVideoSourcesDidChange (if pending) then transitions source-
    // present tiles to 'video' and cancels those grace timers.
    // In the safety timeout path (no audioVideoDidStart), grace timers
    // are the correct behavior — if sources don't arrive within 12s,
    // tiles transition to 'avatar'.
    const { attendeeTileState } = this.getStore();
    for (const attendeeId in attendeeTileState) {
      if (attendeeTileState[attendeeId] === 'frozen') {
        this.processAttendee(attendeeId, 'frozen', 'reconnect_guard');
      }
    }
  }

  // -------------------------------------------------------------------------
  // Roster sync (Effect 1)
  // -------------------------------------------------------------------------

  syncRoster(roster: RosterType, localAttendeeId: string | null): void {
    // Cancel roster removal timers for attendees who reappeared
    for (const attendeeId in roster) {
      const timer = this.rosterTimers.get(attendeeId);
      if (timer) {
        clearTimeout(timer);
        this.rosterTimers.delete(attendeeId);
        // Attendee returned — if they have a source, restore to 'video'
        if (this.sdkSourceSet.has(attendeeId)) {
          this.transitionAttendee(attendeeId, 'video', 'source_reappeared');
        }
      }
    }

    // NOTE: Departure handling (avatar transition + roster timer) is done
    // synchronously in subscribeToPresence(). By the time this useEffect-based
    // syncRoster runs, departed attendees already have roster timers set,
    // so no departure logic is needed here.

    // Add new attendees with initial state 'avatar' (never had video)
    const store = this.getStore();
    const { attendeeTileState } = store;
    const updates: Record<string, TileState> = {};
    let hasNewAttendees = false;
    for (const attendeeId in roster) {
      if (attendeeId === localAttendeeId) continue;
      if (!(attendeeId in attendeeTileState)) {
        // If source already available (race: source arrived before roster),
        // start as 'video'. Otherwise 'avatar'.
        if (this.sdkSourceSet.has(attendeeId)) {
          updates[attendeeId] = 'video';
          this.hadVideoSet.add(attendeeId);
        } else {
          updates[attendeeId] = 'avatar';
        }
        hasNewAttendees = true;
      }
    }

    if (hasNewAttendees) {
      store.setState((s: any) => ({
        attendeeTileState: { ...s.attendeeTileState, ...updates },
      }));
      // Log first appearances after the commit so the diff reflects what
      // actually got written.
      for (const attendeeId in updates) {
        this._debugTransition({
          attendeeId,
          from: 'absent',
          to: updates[attendeeId],
          reason: 'source_new',
        });
      }
    }

    // Sync roster to store
    store.setRoster(roster);
  }

  // -------------------------------------------------------------------------
  // Active speaker sync (Effect 2)
  // -------------------------------------------------------------------------

  syncActiveSpeakers(
    activeSpeakers: string[],
    roster: RosterType,
    localAttendeeId: string | null
  ): void {
    const prev = this.previousActiveSpeakers;
    const store = this.getStore();
    const user = store.user;

    const getUserIdFromAttendeeId = (attendeeId: string): string => {
      if (attendeeId === localAttendeeId) {
        return user?.id?.toString() || attendeeId;
      }
      const attendee = roster[attendeeId];
      if (attendee?.externalUserId) {
        const namePart = attendee.externalUserId.split("-TS-")[0];
        return namePart.replace("-", "_");
      }
      return attendeeId;
    };

    // Emit analytics events for speaker transitions
    for (const speaker of activeSpeakers) {
      if (!prev.includes(speaker)) {
        videoEventBus.emitEvent("videochat:active:speaker:started", {
          userId: getUserIdFromAttendeeId(speaker),
        });
      }
    }
    for (const speaker of prev) {
      if (!activeSpeakers.includes(speaker)) {
        videoEventBus.emitEvent("videochat:active:speaker:stopped", {
          userId: getUserIdFromAttendeeId(speaker),
        });
      }
    }

    store.setActiveSpeakers(activeSpeakers);
    this.previousActiveSpeakers = activeSpeakers;
  }

  // -------------------------------------------------------------------------
  // AudioVideoObserver factory (Effect 3)
  // -------------------------------------------------------------------------

  createAudioVideoObserver(): AudioVideoObserver {
    const localAttendeeId = this.getLocalAttendeeId();

    const observer: AudioVideoObserver = {
      audioVideoDidStartConnecting: (reconnecting: boolean): void => {
        if (reconnecting) this.startReconnectGuard();
      },

      audioVideoDidStop: (sessionStatus: MeetingSessionStatus): void => {
        if (!sessionStatus.isTerminal()) this.startReconnectGuard();
      },

      audioVideoDidStart: (): void => {
        const pending = this.pendingSources;
        this.pendingSources = null;
        this.clearReconnectGuard();

        if (pending) {
          // Reconcile: filter sources against active tiles
          const activeTileAttendees = new Set(
            this.tileAttendeeMap.values()
          );
          const reconciledSources = pending.filter((vs) => {
            const id = vs.attendee.attendeeId;
            return (
              activeTileAttendees.has(id) ||
              !(id in this.getStore().attendeeTileState)
            );
          });
          observer.remoteVideoSourcesDidChange?.(reconciledSources);
        }
      },

      connectionDidBecomeGood: () => {
        if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
        this.recoveryTimer = setTimeout(() => {
          this.recoveryTimer = null;
          this.setConnectionQualityWithWindowClear('good');

          // Re-evaluate all frozen attendees — some may now start grace timers
          const { attendeeTileState } = this.getStore();
          for (const attendeeId in attendeeTileState) {
            if (attendeeTileState[attendeeId] === 'frozen') {
              this.processAttendee(attendeeId, 'frozen', 'connection_good');
            }
          }
        }, RECOVERY_COOLDOWN_MS);
      },

      connectionDidBecomePoor: () => {
        if (this.recoveryTimer) {
          clearTimeout(this.recoveryTimer);
          this.recoveryTimer = null;
        }
        this.setConnectionQualityWithWindowClear('poor');

        // Cancel all pending grace timers — stay frozen during degraded quality
        for (const [, timer] of this.graceTimers) {
          clearTimeout(timer);
        }
        this.graceTimers.clear();
      },

      connectionDidSuggestStopVideo: () => {
        if (this.recoveryTimer) {
          clearTimeout(this.recoveryTimer);
          this.recoveryTimer = null;
        }
        this.setConnectionQualityWithWindowClear('suggestStopVideo');

        for (const [, timer] of this.graceTimers) {
          clearTimeout(timer);
        }
        this.graceTimers.clear();
      },

      videoTileDidUpdate: (tileState): void => {
        if (
          tileState.tileId &&
          !tileState.localTile &&
          tileState.boundAttendeeId
        ) {
          this.tileAttendeeMap.set(
            tileState.tileId,
            tileState.boundAttendeeId
          );
        }
      },

      videoTileWasRemoved: (tileId: number): void => {
        const attendeeId = this.tileAttendeeMap.get(tileId);
        this.tileAttendeeMap.delete(tileId);
        if (!attendeeId) return;
        if (localAttendeeId && attendeeId === localAttendeeId) return;

        // Simulcast/SFU recycle — attendee still in SDK source set
        if (this.sdkSourceSet.has(attendeeId)) return;

        const { attendeeTileState } = this.getStore();
        const currentState = attendeeTileState[attendeeId];
        if (!currentState || currentState === 'absent') return;

        this.processAttendee(attendeeId, currentState, 'sdk_tile_removed');
      },

      remoteVideoSourcesDidChange: (videoSources: VideoSource[]): void => {
        if (this.isReconnecting) {
          this.pendingSources = videoSources;
          return;
        }

        const videoSourceIdSet = new Set(
          videoSources.map((vs) => vs.attendee.attendeeId)
        );
        const previousSdkSources = this.sdkSourceSet;
        this.sdkSourceSet = videoSourceIdSet;

        // Mass-drop detection
        let droppedCount = 0;
        for (const id of previousSdkSources) {
          if (!videoSourceIdSet.has(id)) droppedCount++;
        }
        const isTotalSourceLoss =
          previousSdkSources.size > 0 && videoSourceIdSet.size === 0;
        const isMassDrop = isTotalSourceLoss || droppedCount >= 2;
        if (isMassDrop) {
          if (this.connectionQuality === 'good') {
            this.setConnectionQualityWithWindowClear('poor');
            if (this.massDropTimer) clearTimeout(this.massDropTimer);
            this.massDropTimer = setTimeout(() => {
              this.massDropTimer = null;
              if (
                this.connectionQuality === 'poor' &&
                !this.isReconnecting
              ) {
                this.setConnectionQualityWithWindowClear('good');
                // Re-evaluate frozen attendees (same as connectionDidBecomeGood)
                const { attendeeTileState } = this.getStore();
                for (const id in attendeeTileState) {
                  if (attendeeTileState[id] === 'frozen') {
                    this.processAttendee(id, 'frozen', 'connection_good');
                  }
                }
              }
            }, MASS_DROP_VALIDATION_MS);
          }
        }

        // Build cameraSources for downlink hooks
        const cameraSources: string[] = [];
        for (const attendeeId of videoSourceIdSet) {
          if (
            !isContentShare(attendeeId) &&
            attendeeId !== localAttendeeId
          ) {
            cameraSources.push(attendeeId);
          }
        }

        // Per-attendee transition reasons captured during the transform.
        // Drained AFTER the atomic setState commit, so the diff against
        // committed state matches what the store actually holds.
        const transitionReasons = new Map<string, TileTransitionReason>();
        const previousTileState: Record<string, TileState | undefined> = {
          ...this.getStore().attendeeTileState,
        };

        // Atomic update: attendeeTileState + videoSourceState
        const store = this.getStore();
        store.setState((s: any) => {
          const nextTileState: Record<string, TileState> = {
            ...s.attendeeTileState,
          };

          // Process each known attendee
          for (const attendeeId in nextTileState) {
            if (localAttendeeId && attendeeId === localAttendeeId) continue;
            if (nextTileState[attendeeId] === 'absent') continue;
            // Skip attendees in roster grace — their visual state (avatar or
            // frozen) is already correct and should not be overridden by
            // source-loss processing during teardown.
            if (this.rosterTimers.has(attendeeId)) continue;

            if (videoSourceIdSet.has(attendeeId)) {
              // Source present
              this.hadVideoSet.add(attendeeId);
              const graceTimer = this.graceTimers.get(attendeeId);
              if (graceTimer) {
                clearTimeout(graceTimer);
                this.graceTimers.delete(attendeeId);
              }
              nextTileState[attendeeId] = this.bandwidthPaused.has(attendeeId)
                ? 'frozen'
                : 'video';
              if (previousSdkSources.has(attendeeId)) {
                transitionReasons.set(attendeeId, 'source_reappeared');
              } else {
                transitionReasons.set(attendeeId, 'source_new');
              }
            } else if (isContentShare(attendeeId)) {
              // Content share gone — remove
              delete nextTileState[attendeeId];
              this.hadVideoSet.delete(attendeeId);
              transitionReasons.set(
                attendeeId,
                isMassDrop ? 'mass_drop' : 'source_lost'
              );
            } else if (previousSdkSources.has(attendeeId)) {
              // Source just disappeared — resolve new state
              const intent = this.intentData.get(attendeeId);
              const intentFresh =
                intent !== undefined &&
                Date.now() - intent.lastUpdate <= INTENT_STALENESS_MS;

              if (intent?.cameraIntentOn === false && intentFresh) {
                nextTileState[attendeeId] = 'avatar';
                const graceTimer = this.graceTimers.get(attendeeId);
                if (graceTimer) {
                  clearTimeout(graceTimer);
                  this.graceTimers.delete(attendeeId);
                }
              } else if (intent?.cameraIntentOn === true && intentFresh) {
                nextTileState[attendeeId] = 'frozen';
              } else if (this.connectionQuality !== 'good') {
                nextTileState[attendeeId] = 'frozen';
              } else if (this.hadVideoSet.has(attendeeId)) {
                // Had video, good connection, no intent → grace timer
                nextTileState[attendeeId] = 'frozen';
                // Schedule grace timer (outside setState)
              } else {
                nextTileState[attendeeId] = 'avatar';
              }
              transitionReasons.set(
                attendeeId,
                isMassDrop ? 'mass_drop' : 'source_lost'
              );
            }
          }

          // Add new attendees from sources not yet in tile state
          for (const attendeeId of videoSourceIdSet) {
            if (!(attendeeId in nextTileState) && !isContentShare(attendeeId)) {
              this.hadVideoSet.add(attendeeId);
              nextTileState[attendeeId] = this.bandwidthPaused.has(attendeeId)
                ? 'frozen'
                : 'video';
              transitionReasons.set(attendeeId, 'source_new');
            }
          }

          // Filter activeSpeakersWithCameraSource
          const activeSpeakersWithCameraSource =
            s.videoSourceState.activeSpeakersWithCameraSource.filter(
              (id: string) => {
                const ts = nextTileState[id];
                return ts === 'video' || ts === 'frozen';
              }
            );

          const nextVideoSourceState: VideoSourceState = {
            ...s.videoSourceState,
            cameraSources,
            activeSpeakersWithCameraSource,
          };

          return {
            attendeeTileState: nextTileState,
            videoSourceState: nextVideoSourceState,
          };
        });

        // Diff and log every real transition. Reads committed state so
        // mid-batch values can't leak into the log.
        {
          const committed = this.getStore().attendeeTileState;
          const seen = new Set<string>();
          for (const attendeeId in committed) {
            seen.add(attendeeId);
            const from = previousTileState[attendeeId] ?? 'absent';
            const to = committed[attendeeId];
            const reason = transitionReasons.get(attendeeId);
            if (reason && from !== to) {
              this._debugTransition({ attendeeId, from, to, reason });
            }
          }
          // Attendees that existed before and are now removed (content share
          // teardown) — log to → 'absent'.
          for (const attendeeId in previousTileState) {
            if (seen.has(attendeeId)) continue;
            const from = previousTileState[attendeeId];
            if (!from || from === 'absent') continue;
            const reason = transitionReasons.get(attendeeId);
            if (reason) {
              this._debugTransition({
                attendeeId,
                from,
                to: 'absent',
                reason,
              });
            }
          }
        }

        // Start grace timers for attendees that disappeared and had video
        // (must be done after setState to avoid inconsistency)
        for (const id of previousSdkSources) {
          if (videoSourceIdSet.has(id)) continue;
          if (isContentShare(id)) continue;
          if (this.rosterTimers.has(id)) continue;
          if (
            this.hadVideoSet.has(id) &&
            this.connectionQuality === 'good'
          ) {
            const intent = this.intentData.get(id);
            const intentFresh =
              intent !== undefined &&
              Date.now() - intent.lastUpdate <= INTENT_STALENESS_MS;
            // Only start grace if no fresh intent already resolved it
            if (
              !(intent?.cameraIntentOn === false && intentFresh) &&
              !(intent?.cameraIntentOn === true && intentFresh)
            ) {
              this.startGraceTimer(id);
            }
          }
        }

        // Update downlink preferences. Mass-drop self-heal is the only
        // post-source-change path that truly cannot wait for hysteresis —
        // a 3 s delay during a recovery window is worse than racing the
        // estimator. Routine source-change updates use hysteresis.
        const isMassDropRecovery = isMassDrop;
        this.applyPreferenceUpdate({ bypassHysteresis: isMassDropRecovery });
      },

      metricsDidReceive: (clientMetricReport: ClientMetricReport): void => {
        try {
          const global = clientMetricReport.getObservableMetrics();
          const videoMetrics = clientMetricReport.getObservableVideoMetrics();

          // With simulcast enabled, getObservableVideoMetrics() returns one
          // entry per SSRC — three for self (low/medium/high layers) and one
          // per remote. A naive last-wins pass produces non-deterministic
          // resolution values. Aggregate instead:
          //   outbound: sum(bitrate), max(fps/width/height) across self SSRCs
          //   inbound:  sum(bitrate), max(width/height), max(packet_loss)
          //             across remote SSRCs
          // Caveat: outbound_video_width reports the encoder's top layer. If
          // the SFU drops that layer under receiver bandwidth pressure the
          // encoder still produces 1280×720 locally, so this metric won't
          // reveal SFU-side top-layer drops (that needs qualityLimitationReason
          // from RTCStatsReport, not exposed in the observable spec).
          let outboundBitrate: number | null = null;
          let outboundFps: number | null = null;
          let outboundWidth: number | null = null;
          let outboundHeight: number | null = null;

          let inboundBitrate: number | null = null;
          let inboundWidth: number | null = null;
          let inboundHeight: number | null = null;
          let inboundPacketLoss: number | null = null;

          if (videoMetrics) {
            for (const [, ssrcMap] of Object.entries(videoMetrics)) {
              for (const [, metrics] of Object.entries(ssrcMap)) {
                const m = metrics as Record<string, number>;

                // Upstream (outbound) — aggregate across simulcast layers
                if (m.videoUpstreamBitrate !== undefined) {
                  outboundBitrate = (outboundBitrate ?? 0) + m.videoUpstreamBitrate;
                  if (m.videoUpstreamFramesEncodedPerSecond != null) {
                    outboundFps = Math.max(outboundFps ?? 0, m.videoUpstreamFramesEncodedPerSecond);
                  }
                  if (m.videoUpstreamFrameWidth != null) {
                    outboundWidth = Math.max(outboundWidth ?? 0, m.videoUpstreamFrameWidth);
                  }
                  if (m.videoUpstreamFrameHeight != null) {
                    outboundHeight = Math.max(outboundHeight ?? 0, m.videoUpstreamFrameHeight);
                  }
                }

                // Downstream (inbound) — sum bitrate, max resolution & packet loss
                if (m.videoDownstreamBitrate !== undefined) {
                  inboundBitrate = (inboundBitrate ?? 0) + m.videoDownstreamBitrate;
                }
                if (m.videoDownstreamFrameWidth != null) {
                  inboundWidth = Math.max(inboundWidth ?? 0, m.videoDownstreamFrameWidth);
                }
                if (m.videoDownstreamFrameHeight != null) {
                  inboundHeight = Math.max(inboundHeight ?? 0, m.videoDownstreamFrameHeight);
                }
                if (m.videoDownstreamPacketLossPercent !== undefined) {
                  inboundPacketLoss = inboundPacketLoss === null
                    ? m.videoDownstreamPacketLossPercent
                    : Math.max(inboundPacketLoss, m.videoDownstreamPacketLossPercent);
                }
              }
            }
          }

          latestQualityMetrics = {
            rtt_ms: global.audioUpstreamRoundTripTimeMs ?? null,
            available_outgoing_bitrate: global.availableOutgoingBitrate ?? null,
            available_incoming_bitrate: global.availableIncomingBitrate ?? null,
            inbound_audio_packet_loss: global.audioPacketsReceivedFractionLossPercent != null
              ? global.audioPacketsReceivedFractionLossPercent
              : null,
            inbound_audio_jitter: global.audioDownstreamJitterMs ?? null,
            outbound_video_bitrate: outboundBitrate,
            outbound_video_fps: outboundFps,
            outbound_video_width: outboundWidth,
            outbound_video_height: outboundHeight,
            inbound_video_packet_loss: inboundPacketLoss,
            inbound_video_width: inboundWidth,
            inbound_video_height: inboundHeight,
            inbound_video_bitrate: inboundBitrate,
            outbound_video_quality_limitation: null, // Only available from RTCStatsReport
            inbound_video_jitter: null, // Not exposed by observable metrics
            inbound_video_frames_dropped: null, // Not exposed by observable metrics
            outbound_audio_bitrate: null, // Not exposed by observable metrics
          };

          // Rolling window for heartbeat-tick recovery (plan Step 4 cause #1).
          // Single write path — same call as latestQualityMetrics; they
          // cannot drift. FIFO, max RECOVERY_WINDOW_SIZE samples.
          recentQualitySamples.push(latestQualityMetrics);
          if (recentQualitySamples.length > RECOVERY_WINDOW_SIZE) {
            recentQualitySamples = recentQualitySamples.slice(-RECOVERY_WINDOW_SIZE);
          }
        } catch {
          // Non-critical — don't break the observer chain
        }
      },
    };

    return observer;
  }

  // -------------------------------------------------------------------------
  // VideoDownlinkObserver factory (Effect 3)
  // -------------------------------------------------------------------------

  createDownlinkObserver(
    audioVideo: { getVideoTile: (tileId: number) => any }
  ): VideoDownlinkObserver {
    return {
      tileWillBePausedByDownlinkPolicy: (tileId: number) => {
        const attendeeId = audioVideo
          .getVideoTile(tileId)
          ?.state().boundAttendeeId;
        if (!attendeeId) return;
        // Self-view safety: never enter bandwidth_suppressed for local.
        const localId = this.getLocalAttendeeId();
        if (localId && attendeeId === localId) return;
        this.bandwidthPaused.add(attendeeId);
        if (this.rosterTimers.has(attendeeId)) return;
        const store = this.getStore();
        const currentState = store.attendeeTileState[attendeeId];
        if (currentState !== 'video' && currentState !== 'frozen') return;
        // Suppression intent decides whether this is a deliberate suppression
        // (bandwidth_suppressed: avatar + pill, no grace) or an incidental
        // SDK-level pause (frozen: keeps last canvas frame visible).
        const targetState: TileState = this.suppressionIntent.has(attendeeId)
          ? 'bandwidth_suppressed'
          : 'frozen';
        if (currentState === targetState) return;
        // Cancel grace timer if entering bandwidth_suppressed — suppression
        // is deliberate, not transient. Grace would race against unpause.
        if (targetState === 'bandwidth_suppressed') {
          const graceTimer = this.graceTimers.get(attendeeId);
          if (graceTimer) {
            clearTimeout(graceTimer);
            this.graceTimers.delete(attendeeId);
          }
        }
        this._debugTransition({
          attendeeId,
          from: currentState,
          to: targetState,
          reason: 'bandwidth_paused',
        });
        store.setAttendeeTileState(attendeeId, targetState);
      },
      tileWillBeUnpausedByDownlinkPolicy: (tileId: number) => {
        const attendeeId = audioVideo
          .getVideoTile(tileId)
          ?.state().boundAttendeeId;
        if (!attendeeId) return;
        this.bandwidthPaused.delete(attendeeId);
        if (this.rosterTimers.has(attendeeId)) return;
        if (this.sdkSourceSet.has(attendeeId)) {
          const store = this.getStore();
          const currentState = store.attendeeTileState[attendeeId];
          if (currentState === 'frozen' || currentState === 'bandwidth_suppressed') {
            // Both recover directly to 'video'. For bandwidth_suppressed
            // specifically, no grace timer — the SDK firing this callback
            // IS the confirmation that frames are flowing again.
            this._debugTransition({
              attendeeId,
              from: currentState,
              to: 'video',
              reason: 'bandwidth_resumed',
            });
            store.setAttendeeTileState(attendeeId, 'video');
          }
        }
      },
    };
  }

  // -------------------------------------------------------------------------
  // Data message handler (Effect 4)
  // -------------------------------------------------------------------------

  handleDataMessage(message: { senderAttendeeId: string; text: () => string }): void {
    const localAttendeeId = this.getLocalAttendeeId();
    const senderId = message.senderAttendeeId;
    if (!senderId || senderId === localAttendeeId) return;

    try {
      const payload: DeviceStateMessage = JSON.parse(message.text());
      const current = this.intentData.get(senderId);

      // Timestamp ordering — keep latest, discard stale
      if (current && current.lastUpdate > payload.timestamp) return;

      this.intentData.set(senderId, {
        cameraIntentOn: payload.camera,
        micIntentOn: payload.mic,
        lastUpdate: payload.timestamp,
      });

      // Skip visual transitions for attendees in roster grace — their state
      // is locked to avatar. intentData is still updated above for reconnects.
      if (this.rosterTimers.has(senderId)) return;

      // If intent says camera on and attendee is in 'avatar', transition to
      // 'frozen' only if they previously had video (valid canvas frame exists).
      // Otherwise stay in 'avatar' and let remoteVideoSourcesDidChange
      // transition directly to 'video' when the source arrives.
      if (payload.camera) {
        const { attendeeTileState } = this.getStore();
        if (
          attendeeTileState[senderId] === 'avatar' &&
          this.hadVideoSet.has(senderId)
        ) {
          this.transitionAttendee(senderId, 'frozen', 'intent_camera_on');
        }
      }

      // If intent says camera off and there's a pending grace timer, fire immediately
      if (!payload.camera) {
        const graceTimer = this.graceTimers.get(senderId);
        if (graceTimer) {
          clearTimeout(graceTimer);
          this.graceTimers.delete(senderId);
          this.hadVideoSet.delete(senderId);
          const store = this.getStore();
          const previousState = store.attendeeTileState[senderId] ?? 'absent';
          this._debugTransition({
            attendeeId: senderId,
            from: previousState,
            to: 'avatar',
            reason: 'intent_camera_off',
          });
          store.setAttendeeTileState(senderId, 'avatar');
        } else {
          // Also transition if currently frozen and source is gone
          const store = this.getStore();
          if (
            store.attendeeTileState[senderId] === 'frozen' &&
            !this.sdkSourceSet.has(senderId)
          ) {
            this.hadVideoSet.delete(senderId);
            this._debugTransition({
              attendeeId: senderId,
              from: 'frozen',
              to: 'avatar',
              reason: 'intent_camera_off',
            });
            store.setAttendeeTileState(senderId, 'avatar');
          }
        }
      }

      if (process.env.NODE_ENV === 'development') {
        console.debug(
          `[ChimeObserverState] intent received from ${senderId}:`,
          `camera=${payload.camera}`,
          `mic=${payload.mic}`
        );
      }
    } catch {
      // Malformed message — ignore
    }
  }

  // -------------------------------------------------------------------------
  // Emoji reactions handler
  // -------------------------------------------------------------------------

  /**
   * Handle an incoming "emoji-reaction" data message. Reactions are ephemeral
   * peer-to-peer UI signals with a 4-second badge lifetime, tracked in
   * a dedicated `reactionTimers` set so source-loss semantics in `graceTimers`
   * don't prematurely cancel them.
   */
  handleReactionMessage(message: { senderAttendeeId: string; text: () => string }): void {
    const localAttendeeId = this.getLocalAttendeeId();
    const senderId = message.senderAttendeeId;
    // Belt-and-suspenders: Chime's realtimeSendDataMessage does not currently
    // echo to the sender, but guard against future SDK behavior changes.
    if (!senderId || senderId === localAttendeeId) return;

    let payload: ReactionEvent;
    try {
      payload = JSON.parse(message.text()) as ReactionEvent;
    } catch {
      return; // malformed — drop silently
    }

    if (
      typeof payload?.emoji !== "string" ||
      typeof payload?.id !== "string" ||
      typeof payload?.attendeeId !== "string" ||
      typeof payload?.timestamp !== "number"
    ) {
      return;
    }

    const store = this.getStore() as unknown as {
      addReaction: (attendeeId: string, reaction: ReactionEvent) => void;
      removeReaction: (attendeeId: string, reactionId: string) => void;
    };
    store.addReaction(senderId, payload);

    const timer = setTimeout(() => {
      store.removeReaction(senderId, payload.id);
      this.reactionTimers.delete(timer);
    }, 4_000);
    this.reactionTimers.add(timer);

    videoEventBus.emitEvent("videochat:reaction:received", {
      emoji: payload.emoji,
      senderAttendeeId: senderId,
    });
  }

  // -------------------------------------------------------------------------
  // Cleanup
  // -------------------------------------------------------------------------

  dispose(): void {
    for (const timer of this.graceTimers.values()) clearTimeout(timer);
    this.graceTimers.clear();
    for (const timer of this.rosterTimers.values()) clearTimeout(timer);
    this.rosterTimers.clear();
    for (const timer of this.reactionTimers) clearTimeout(timer);
    this.reactionTimers.clear();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.massDropTimer) {
      clearTimeout(this.massDropTimer);
      this.massDropTimer = null;
    }
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
      this.recoveryTimer = null;
    }
    if (this.preemptiveTimer) {
      clearTimeout(this.preemptiveTimer);
      this.preemptiveTimer = null;
    }
  }
}
